import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Badge, H3, Spinner, Tabs, useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import { useDataViews } from '../../hooks';
import { ChangeStatusModal } from '../change_status_modal/change_status_modal.component';
import { DetailsTab } from '../details_tab/details_tab.component';
import { DimensionsTab } from '../dimensions_tab/dimensions_tab.component';
import { EditDetailsModal } from '../edit_details_modal/edit_details_modal.component';
import { MeasuresTab } from '../measures_tab/measures_tab.component';
import { SettingsModal } from '../settings_modal/settings_modal.component';
export const ViewDetail = () => {
    const { viewId } = useParams();
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const { fetchDataViewById, isLoading: isApiLoading } = useDataViews();
    const [dataView, setDataView] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [isChangeStatusModalOpen, openChangeStatusModal, closeChangeStatusModal] = useBooleanState();
    const [isSettingsModalOpen, openSettingsModal, closeSettingsModal] = useBooleanState();
    const [isEditDetailsModalOpen, openEditDetailsModal, closeEditDetailsModal] = useBooleanState();
    useEffect(() => {
        if (!viewId) {
            navigate('/library/data-views');
            return;
        }
        let isMounted = true;
        const loadDataView = async () => {
            setIsLoading(true);
            try {
                const view = await fetchDataViewById(viewId);
                if (isMounted) {
                    setDataView(view);
                }
            }
            catch (error) {
                // Error already handled in hook
                if (isMounted) {
                    navigate('/library/data-views');
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadDataView();
        return () => {
            isMounted = false;
        };
    }, [viewId]);
    const handleStatusChange = useCallback((updatedView) => {
        setDataView(updatedView);
    }, []);
    const handleSettingsSave = useCallback((updatedView) => {
        setDataView(updatedView);
    }, []);
    const handleDetailsSave = useCallback((updatedView) => {
        setDataView(updatedView);
    }, []);
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'DRAFT':
                return 'warning';
            case 'IN_REVIEW':
                return 'incomplete';
            case 'PUBLISHED':
                return 'success';
            default:
                return 'common';
        }
    };
    const getStatusLabel = (status) => {
        switch (status) {
            case 'DRAFT':
                return translate('In Progress');
            case 'IN_REVIEW':
                return translate('In Review');
            case 'PUBLISHED':
                return translate('Ready for Use');
            default:
                return status;
        }
    };
    const breadcrumbsPages = useMemo(() => [
        { name: translate('Datasets'), path: '/library/data-views' },
        {
            name: dataView?.name || '',
            path: `/library/data-views/${viewId}`,
        },
    ], [dataView?.name, viewId, translate]);
    // Check if the dataset is editable (only DRAFT status can be edited)
    const isEditable = useMemo(() => {
        return dataView?.status === 'DRAFT';
    }, [dataView?.status]);
    const tabItems = useMemo(() => [
        {
            content: dataView ? (_jsx(DetailsTab, { dataView: dataView, onEditDetails: openEditDetailsModal, isEditable: isEditable })) : null,
            label: translate('Details'),
        },
        {
            content: dataView ? (_jsx(DimensionsTab, { dataView: dataView, onDataViewUpdate: setDataView, isLoading: isLoading || isApiLoading })) : null,
            label: translate('Dimensions'),
        },
        {
            content: dataView ? (_jsx(MeasuresTab, { dataView: dataView, onDataViewUpdate: setDataView, isLoading: isLoading || isApiLoading })) : null,
            label: translate('Measures'),
        },
    ], [dataView, translate, isLoading, isApiLoading, openEditDetailsModal, isEditable]);
    // Check if dimensions or measures exist
    const hasDimensionsOrMeasures = useMemo(() => {
        if (!dataView) {
            return false;
        }
        const dimensionsCount = Object.keys(dataView.data?.dimensions || {}).length;
        const measuresCount = Object.keys(dataView.data?.measures || {}).length;
        return dimensionsCount > 0 || measuresCount > 0;
    }, [dataView]);
    const headerOptions = useMemo(() => {
        if (!dataView) {
            return [];
        }
        const options = [];
        // Change status button - always shown, disabled until dimensions and/or measures are added
        options.push({
            name: translate('Change status'),
            onClick: openChangeStatusModal,
            variant: 'secondary',
            disabled: isApiLoading || !hasDimensionsOrMeasures,
        });
        // Data settings button - disabled for published and in-review datasets
        options.push({
            name: translate('Data settings'),
            onClick: openSettingsModal,
            variant: 'secondary',
            disabled: isApiLoading || dataView.status === 'PUBLISHED' || dataView.status === 'IN_REVIEW',
        });
        return options;
    }, [dataView, translate, openChangeStatusModal, isApiLoading, hasDimensionsOrMeasures]);
    if (!dataView) {
        return (_jsx("section", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) }));
    }
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, LinkComponent: Link, title: _jsxs("div", { className: 'flex items-center gap-x-2', children: [_jsx(H3, { children: dataView.name }), _jsx(Badge, { variant: getStatusBadgeVariant(dataView.status), label: getStatusLabel(dataView.status) }), _jsx(Badge, { variant: 'common', label: `v${dataView.version}` })] }), subtitle: dataView.metadata?.description, options: headerOptions }), _jsx(Tabs, { selectedIndex: activeTab, onTabChange: setActiveTab, tabItems: tabItems }), isChangeStatusModalOpen && dataView && (_jsx(ChangeStatusModal, { dataView: dataView, onClose: closeChangeStatusModal, onStatusChange: handleStatusChange })), isSettingsModalOpen && dataView && (_jsx(SettingsModal, { isOpen: isSettingsModalOpen, onClose: closeSettingsModal, onSave: handleSettingsSave, dataView: dataView, isLoading: isApiLoading })), isEditDetailsModalOpen && dataView && (_jsx(EditDetailsModal, { isOpen: isEditDetailsModalOpen, onClose: closeEditDetailsModal, onSave: handleDetailsSave, dataView: dataView }))] }));
};
